import React, { FC, useState } from "react";
import styled from "styled-components";
import {
  TagManager,
  TagManagerEntityTypes,
} from "../../../../../../../components/tags-manager";
import {
  Button,
  ButtonVariant,
  ModalHeader,
  Typography,
  TypographyVariant,
} from "../../../../../../../components/ui-kit";
import { ExternalAppApiType } from "../../../../../../../util/types";
import { useSetupWholesaleTagsForExternalApp } from "../../hooks/useSetupWholesaleTagsForExternalApp";

const Wrapper = styled.div`
  width: 700px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 30px 20px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 15px;
`;

const Divider = styled.div`
  display: flex;
  height: 1px;
  background-color: #eee;
  margin: 30px 0;
`;

interface ThisProps {
  appData: ExternalAppApiType;
}

export const SetupWholesaleTagsModal: FC<ThisProps> = ({ appData }) => {
  const { setupWholesaleTags, loading } = useSetupWholesaleTagsForExternalApp();
  const [wholesaleOrderTags, setWholesaleOrderTags] = useState<string[]>(
    appData.wholesaleOrderTags ?? []
  );
  const [wholesaleCustomerTags, setWholesaleCustomerTags] = useState<string[]>(
    appData.wholesaleCustomerTags ?? []
  );

  const handleConfirmWholesaleTagsSetup = async () => {
    await setupWholesaleTags({
      id: appData._id,
      data: {
        wholesaleOrderTags: wholesaleOrderTags ?? [],
        wholesaleCustomerTags: wholesaleCustomerTags ?? [],
      },
    });
  };

  const handleCancel = async () => {
    await setupWholesaleTags({
      id: appData._id,
      data: {
        wholesaleOrderTags: [...(appData.wholesaleOrderTags ?? [])],
        wholesaleCustomerTags: [...(appData.wholesaleCustomerTags ?? [])],
      },
      withoutSuccessMessage: true,
    });
  };

  return (
    <Wrapper>
      <ModalHeader title={"Setup wholesale tags" + ` - (${appData.name})`} />
      <Content>
        <Typography
          variant={TypographyVariant.HEADER2}
          style={{ marginBottom: 10 }}
        >
          Define Wholesale Order Tags
        </Typography>
        <Typography
          variant={TypographyVariant.HEADLINE}
          style={{ marginBottom: 20 }}
        >
          Choose which order tags should be used to identify wholesale orders
          from your Shopify store. Any order containing one of these tags will
          be treated as a wholesale order on Fena Toolkit.
        </Typography>
        <TagManager
          entityType={TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS}
          entityId={appData._id}
          tags={wholesaleOrderTags}
          isB2bTagSettings
          externalSetTagsFunction={setWholesaleOrderTags}
        />

        <Divider />

        <Typography
          variant={TypographyVariant.HEADER2}
          style={{ marginBottom: 10 }}
        >
          Define Wholesale Customer Tags
        </Typography>
        <Typography
          variant={TypographyVariant.HEADLINE}
          style={{ marginBottom: 20 }}
        >
          Choose which customer tags should be used to identify wholesale
          customers from your Shopify store. Any customer containing one of
          these tags will be treated as a wholesale customer on Fena Toolkit.
        </Typography>
        <TagManager
          entityType={TagManagerEntityTypes.APP_SETUP_WHOLESALE_TAGS}
          entityId={appData._id}
          tags={wholesaleCustomerTags}
          isB2bTagSettings
          externalSetTagsFunction={setWholesaleCustomerTags}
        />

        <Buttons>
          <Button
            disabled={loading}
            variant={ButtonVariant.OUTLINED}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button disabled={loading} onClick={handleConfirmWholesaleTagsSetup}>
            Confirm
          </Button>
        </Buttons>
      </Content>
    </Wrapper>
  );
};
